import { TreeSelect } from "antd";
import { useMemo } from "react";
import { FiscalPeriod } from "../../repository/data.default";

export const ALL_TIME = "all";

export interface FiscalPeriodSelectProps {
  readonly periods: FiscalPeriod[];
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

interface TreeNode {
  title: string;
  value: string;
  key: string;
  children?: TreeNode[];
}

// Builds a Year -> Semester -> Quarter tree from a flat FiscalPeriod[] (roots = periods with no
// parent). Includes an "All time" pseudo-node. Selecting any node returns its uid (or "all").
function FiscalPeriodSelect(props: FiscalPeriodSelectProps) {
  const treeData = useMemo<TreeNode[]>(() => {
    const byParent = new Map<string | undefined, FiscalPeriod[]>();
    for (const p of props.periods) {
      const key = p.parentFiscalPeriodUid || undefined;
      const list = byParent.get(key) ?? [];
      list.push(p);
      byParent.set(key, list);
    }
    const sortByStart = (a: FiscalPeriod, b: FiscalPeriod) =>
      a.startDate.localeCompare(b.startDate);

    const build = (parentUid: string | undefined): TreeNode[] =>
      [...(byParent.get(parentUid) ?? [])].sort(sortByStart).map((p) => {
        const children = build(p.uid);
        return {
          title: p.name,
          value: p.uid,
          key: p.uid,
          ...(children.length ? { children } : {}),
        };
      });

    return [
      { title: "All time", value: ALL_TIME, key: ALL_TIME },
      ...build(undefined),
    ];
  }, [props.periods]);

  return (
    <TreeSelect
      className={props.className ?? "w-56"}
      disabled={props.disabled}
      value={props.value ?? ALL_TIME}
      onChange={(v) => props.onChange?.(v as string)}
      treeData={treeData}
      treeDefaultExpandAll
      showSearch
      treeNodeFilterProp="title"
      placeholder="Period"
    />
  );
}

export default FiscalPeriodSelect;
